<script setup lang="ts">
/**
 * Footer standard d'un el-dialog / el-drawer : aligne les boutons d'action à
 * droite (gap homogène) avec un bouton « Annuler » optionnel. Remplace les
 * `<span class="dialog-footer">` réécrits localement.
 *
 *   <template #footer>
 *     <DialogActions @cancel="dialog = false">
 *       <el-button type="primary" @click="onConfirm">Valider</el-button>
 *     </DialogActions>
 *   </template>
 *
 * Le slot par défaut = boutons d'action (à droite, après Annuler).
 * Le slot #left = contenu optionnel aligné à gauche (ex. info secondaire).
 */
withDefaults(
  defineProps<{
    /** Libellé du bouton Annuler. */
    cancelLabel?: string;
    /** Afficher le bouton Annuler intégré. */
    showCancel?: boolean;
    /** Désactiver/charger l'annulation. */
    cancelDisabled?: boolean;
  }>(),
  {
    cancelLabel: 'Annuler',
    showCancel: true,
    cancelDisabled: false,
  },
);

defineEmits<{ cancel: [] }>();
</script>

<template>
  <div class="dialog-actions">
    <div v-if="$slots.left" class="dialog-actions-left">
      <slot name="left" />
    </div>
    <div class="dialog-actions-right">
      <el-button v-if="showCancel" :disabled="cancelDisabled" @click="$emit('cancel')">
        {{ cancelLabel }}
      </el-button>
      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.dialog-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-3;
}

// Pas de contenu gauche -> le groupe de droite occupe toute la largeur et
// reste aligné à droite (comportement par défaut des footers el-dialog).
.dialog-actions:not(:has(.dialog-actions-left)) {
  justify-content: flex-end;
}

.dialog-actions-left {
  color: $text-muted;
  font-size: $font-size-sm;
  min-width: 0;
}

.dialog-actions-right {
  display: flex;
  align-items: center;
  gap: $space-2;
  flex-shrink: 0;
}
</style>
